import { useCallback, useMemo, useState } from "react"

import { asArray } from "@/lib/as-array"
import {
  eventType,
  eventsInPeriod,
  receiptsOf,
  step as stepCursor,
  type CalendarView,
  type EventType,
} from "@/lib/calendar-grid"
import { useGetAuthUser, useGetEvents } from "openapi/api/endpoints"
import type { Event, GetEvents200 } from "openapi/api/model"

/**
 * The calendar's data and the state of what it is showing — canvas 33b/33c.
 *
 * Fetching, the cursor, the view and the legend's filter live here; the views
 * render what they are handed. The event dialog stays on the page, because it
 * owns its own form.
 *
 * `GET /events` takes no date range — only `job_id`, `candidate_id`, `limit`
 * and `offset` — so a calendar cannot ask for the month it is drawing and has
 * to filter what it is given. That is a backend ask. It also means the page can
 * silently miss events past the server's default page, which is why `paging` is
 * read back: when the count exceeds what arrived, the calendar says so rather
 * than drawing a quieter month than the one you have.
 */
export function useCalendar() {
  const eventsQuery = useGetEvents()
  const { data: currentUser } = useGetAuthUser()

  const [view, setView] = useState<CalendarView>("month")
  const [cursor, setCursor] = useState(() => new Date())
  /** Types the legend has switched off. 33b: "Click a type to hide it". */
  const [hidden, setHidden] = useState<EventType[]>([])

  const body = eventsQuery.data as GetEvents200 | undefined
  const all = useMemo(() => asArray<Event>(body?.data), [body])

  const events = useMemo(
    () => (hidden.length === 0 ? all : all.filter((event) => !hidden.includes(eventType(event)))),
    [all, hidden],
  )

  const toggleType = useCallback((type: EventType) => {
    setHidden((prev) =>
      prev.includes(type) ? prev.filter((entry) => entry !== type) : [...prev, type],
    )
  }, [])

  const goToday = useCallback(() => setCursor(new Date()), [])
  const stepBy = useCallback(
    (direction: 1 | -1) => setCursor((at) => stepCursor(at, view, direction)),
    [view],
  )

  /**
   * Switching view keeps the day you were looking at rather than snapping to
   * today — going month → week → day should walk you *into* the week you were
   * reading, not away from it.
   */
  const changeView = useCallback((next: CalendarView) => setView(next), [])

  /*
    How many events the server says exist against how many arrived. Only
    meaningful when the server sends paging; absent, the calendar says nothing.
  */
  const count = body?.paging?.count
  const truncated = typeof count === "number" && count > all.length ? count : undefined

  return {
    view,
    setView: changeView,
    cursor,
    setCursor,
    goToday,
    stepBy,
    /*
      The receipts, like the grid, follow the legend: hide a type and it
      leaves the totals too ("the legend hides a type, and the receipts follow
      it" in tests/calendar.spec.ts). An `allEvents` export used to sit here
      claiming the opposite; nothing read it, and it was that comment rather
      than the behaviour that was wrong.
    */
    events,
    hidden,
    toggleType,
    /*
      The receipts describe the period on screen. They were computed from
      every event the page had loaded, so stepping to October kept printing
      September's totals under an "October 2026" heading.

      "Today" is the exception, and deliberately: it is a fact about today,
      not about the period. Scoped with the rest, the October view would say
      "0 today" while today held a full diary.
    */
    receipts: useMemo(
      () => ({
        ...receiptsOf(eventsInPeriod(events, cursor, view)),
        today: receiptsOf(events).today,
      }),
      [events, cursor, view],
    ),
    email: currentUser?.email,
    truncated,
    /*
      Whether the calendar is known at all. `asArray(undefined)` is an empty
      array, so until the request lands every figure reads as a confident zero
      and the grid as a month with nothing in it.
    */
    isLoading: eventsQuery.isPending,
    isError: eventsQuery.isError,
  }
}
